import { IpexMomentEnum } from '@insights/insights-data-provider';
import AnalyticsApi from './AnalyticsApi';
import AnalyticsApiImpl from './AnalyticsApiImpl';
import { AnalyticsPayload } from './AnalyticsEvent';

/**
 * Unitary analytics API.
 *
 * @remarks
 * This API is intended to serve as a unitary, simple, easy-to-use interface through which to report
 * analytics, performance, and stability telemetry to various services.
 *
 * The API is stateless, allowing consumers to report events without having to manage its connection
 * status.
 */
const analytics: AnalyticsApi = new AnalyticsApiImpl();

/**
 * Creates and returns an {@link EventReporter} function. The reporter may be invoked as soon as
 * it's created, regardless of the API's connection status.
 *
 * @param eventName - The name to supply when reporting events.
 * @param ipexMoment - The {@link IpexMomentEnum | IPEX-defined "moment"} corresponding to the
 * event.
 * @typeParam TPayload - Type specification for the event's data payload, if any.
 *
 * @example Creating and using an event reporter
 * ```ts
 * // Create the reporter
 * const reportUndo = reporter('undo', IpexMomentEnum.makeItMe);
 * // Report the 'undo' event
 * reportUndo();
 * ```
 *
 * @example Creating and using an event reporter with payload
 * ```ts
 * // Create the reporter
 * const reportChangeFrameDepth = reporter<{ size: number }>('change_frame_depth', IpexMomentEnum.makeItMe);
 * // Report 'change_frame_depth' event
 * reportChangeFrameDepth({ size: 350 });
 * ```
 */
export const reporter = <TPayload extends void | AnalyticsPayload>(
  eventName: string,
  ipexMoment: IpexMomentEnum
) => analytics.reporter<TPayload>(eventName, ipexMoment);

export default analytics;
